import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Util } from 'src/shared/util/util';

import {
  CHECKLIST_TEMPLATES,
  ChecklistTemplate,
  TemplateItem,
} from 'src/shared/constants/checklist-templates';

@Component({
  templateUrl: 'select-template.html',
  styleUrls: ['select-template.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectTemplatePage implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);
  private toDos = inject(ToDos);
  private rightSidePanelService = inject(RightSidePanelService);
  private onboardingProvider = inject(OnboardingProvider);
  private util = inject(Util);

  loaded = false;
  isRightSideContent = false;
  isCreating = false;

  searchControl = new FormControl('');
  allTemplates: ChecklistTemplate[] = [];
  filteredTemplates: ChecklistTemplate[] = [];

  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
    this.rightSidePanelService.setDialogPageTitle('Select Template');

    this.allTemplates = CHECKLIST_TEMPLATES;
    this.filteredTemplates = this.allTemplates;

    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.filterTemplates(query || '');
        this.cdr.markForCheck();
      });

    this.loaded = true;
    this.cdr.markForCheck();
  }

  filterTemplates(query: string) {
    if (!query) {
      this.filteredTemplates = this.allTemplates;
      return;
    }

    const lowerQuery = query.toLowerCase();
    this.filteredTemplates = this.allTemplates.filter(
      (t) =>
        t.name.toLowerCase().includes(lowerQuery) ||
        t.description.toLowerCase().includes(lowerQuery)
    );
  }

  async selectTemplate(template: ChecklistTemplate) {
    if (this.isCreating) {
      return;
    }
    this.isCreating = true;
    this.cdr.markForCheck();

    try {
      const addressId = parseInt(localStorage.getItem('addressId') || '0', 10);

      // 1. Check for existing lists with the same name and increment if needed
      const existingLists = await this.toDos.getAllToDoListsForAddress(addressId);
      const uniqueTitle = this.getUniqueListTitle(template.name, existingLists);

      // 2. Create the list
      const listPayload = {
        all_addresses: true,
        title: uniqueTitle,
        before_after_photos_state: 'inactive',
        description: template.description,
      };
      const listResponse = await this.toDos.createList(listPayload);
      const taskListId = listResponse.id;
      localStorage.setItem('taskListId', taskListId);

      // 3. Create sections and items with limited concurrency (max 2 at a time)
      await this.processWithConcurrency(template.sections, async (section) => {
        const groupResponse = await this.toDos.addGroup({ name: section.name });
        const groupId = groupResponse.id;
        await this.toDos.updateGroup(groupId, { address_task_list_id: taskListId });

        await this.processWithConcurrency(section.items, async (item: TemplateItem) => {
          const itemPayload = {
            title: item.title,
            note: item.note || '',
            estimated_time: 0,
            is_important: item.is_important || false,
            task_category_options: [],
            address_task_group_ids: [groupId],
            address_id: addressId,
            address_task_list_id: taskListId,
            task_type: 'cleaning',
          };
          await this.toDos.addToDo(addressId, taskListId, itemPayload);
        }, 2);
      }, 2);

      // 4. Mark onboarding as complete
      await this.onboardingProvider.markGoalAsCompleted('toDos');
      this.onboardingProvider.setShowOnboardingOnPage(false);
      this.onboardingProvider.setShowOnboardingInHeader(true);

      // 5. Close dialog and return to getting-started
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError((err as any)?.error?.message || (err as Error).message, 10000);
    } finally {
      this.isCreating = false;
      this.cdr.markForCheck();
    }
  }

  private async processWithConcurrency<T>(
    items: T[],
    processor: (item: T) => Promise<void>,
    maxConcurrent: number
  ): Promise<void> {
    for (let i = 0; i < items.length; i += maxConcurrent) {
      const batch = items.slice(i, i + maxConcurrent);
      await Promise.all(batch.map(processor));
    }
  }

  private getUniqueListTitle(baseTitle: string, existingLists: any[]): string {
    const existingTitles = existingLists.map(list => list.title.toLowerCase());

    if (!existingTitles.includes(baseTitle.toLowerCase())) {
      return baseTitle;
    }

    let counter = 2;
    while (existingTitles.includes(`${baseTitle} ${counter}`.toLowerCase())) {
      counter++;
    }

    return `${baseTitle} ${counter}`;
  }
}
